import { Box } from '@locus-taxy/encore-mui';
import { useClientId } from '../hooks/useClientId';
import { EntityGridViewWrapper } from '@locus-taxy/encore-grid';
import type { FlatAction } from '@locus-taxy/encore-ui-toolkit';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl, GOOGLE_MAPS_API_KEY } from '../config';
import { buildClientPrefixedPath } from '../utils/url';
import { RoutePath } from '../types/categories';
import { useEntityFormAdditionalContext } from '../hooks/useEntityFormAdditionalContext';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../utils/testIdUtils';
import { useRouteContext } from '../hooks/useRouteContext';

interface EntityGridViewProps {
  selectedEntity: string | undefined;
}

type SelectedItem = {
  entityData: {
    id: string;
  };
};

export default function EntityGridView({
  selectedEntity,
}: EntityGridViewProps) {
  const clientId = useClientId();
  const apiBaseUrl = useMemo(() => {
    if (!clientId) return '';
    return getApiBaseUrl(clientId);
  }, [clientId]);
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // Get user context for form additional context
  const { additionalContext: userContext } = useEntityFormAdditionalContext();

  const routeContext = useRouteContext();

  const handleSelectedItemsChange = useCallback(
    (options: { selectedItems: SelectedItem[]; allRowsSelected: boolean }) => {
      setSelectedItems(options.selectedItems);
    },
    []
  );

  const handleEditToursClick = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement>) => {
      if (selectedItems && selectedItems.length > 0) {
        const selectedToursParam = selectedItems
          .map(item => item.entityData.id)
          .join(',');
        const path = buildClientPrefixedPath(RoutePath.TOUR_EDITS, clientId);
        navigate(`${path}?selectedTours=${selectedToursParam}`);
      }
    },
    [selectedItems, clientId, navigate]
  );

  const additionalActions: FlatAction[] = useMemo(() => {
    const actions: FlatAction[] = [];

    if (
      selectedEntity === 'b2cDeliveryTour' &&
      selectedItems &&
      selectedItems.length > 0
    ) {
      actions.push({
        id: 'edit-tours',
        label: 'Edit Tours',
        variant: 'outline',
        onClick: handleEditToursClick,
      });
    }

    return actions;
  }, [selectedEntity, selectedItems, handleEditToursClick]);

  if (!selectedEntity) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexGrow: 1, // Take remaining space in EntityRouteWrapper
        minHeight: 0, // Critical: allows flex child to shrink below content size
        width: '100%', // Full width
        maxWidth: '100%', // Prevent width overflow
        overflow: 'hidden', // Prevent container overflow
      }}
    >
      {/* Main content area with constrained height */}
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0, // Critical: allows flex child to shrink below content size
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          data-testid={getTestId({
            action: TestIdAction.VIEW,
            object: 'table',
            entity: selectedEntity,
            page: 'grid',
          })}
          sx={{
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flexGrow: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <EntityGridViewWrapper
              clientId={clientId}
              selectedEntity={selectedEntity}
              apiBaseUrl={apiBaseUrl}
              googleApiKey={GOOGLE_MAPS_API_KEY}
              additionalActions={additionalActions}
              onSelectedItemsChange={handleSelectedItemsChange}
              routeContext={routeContext}
              additionalContext={userContext}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
